import { Box, Button, Divider, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { ClockWatch, DocSheet, PendingArrowCircle, ChevronRight } from '@vy/ui-design-system';
import { MONTHS, INVOICE_BILLING_STATUSES } from '@vy/types';
import { getSelectorAttribute, invoicesOverviewPageSelectors } from '@vy/app-selectors';
import React, { Suspense, useMemo } from 'react';

import { Price } from '../../../../../components/design/Price';
import { formatDatesDisplay } from '../../invoices-page-utils/date-display-utils';
import { InvoicesHighlightsProps } from './InvoicesHighlights';
import { isSingleItem } from '../../../../../../../../libs/array/src/services';

const highlightsSelectors = invoicesOverviewPageSelectors.highlights;

type DateBasedViewProps = Pick<
  InvoicesHighlightsProps,
  'dates' | 'invoicesCount' | 'invoicesHighlightsSummary' | 'onFilterClick'
>;

type PanelData = {
  label: string;
  count: number;
  total: number;
  icon: React.ReactNode;
  iconBackground: string;
  actionLabel?: string;
  onAction?: () => void;
  countSelector: string;
  amountSelector: string;
};

export const DateBasedView: React.FC<DateBasedViewProps> = ({
  dates,
  invoicesCount,
  invoicesHighlightsSummary,
  onFilterClick,
}) => {
  const monthName = useMemo(() => {
    if (!dates || dates.length === 0) {
      return null;
    }
    if (isSingleItem(dates)) {
      const monthIndex = parseInt(dates[0]?.month || '', 10);
      return MONTHS[monthIndex] || null;
    }
    return null;
  }, [dates]);

  const dateDisplay = useMemo(() => {
    if (!dates || dates.length === 0) {
      return null;
    }
    return formatDatesDisplay(dates);
  }, [dates]);

  const panels: PanelData[] = useMemo(
    () => [
      {
        label: 'In Review',
        count: invoicesHighlightsSummary?.inReview.count || 0,
        total: invoicesHighlightsSummary?.inReview.amount || 0,
        icon: <DocSheet />,
        iconBackground: '#E7F2FF',
        actionLabel: 'Approve',
        countSelector: highlightsSelectors.inReviewCount,
        amountSelector: highlightsSelectors.inReviewAmount,
        onAction: () => {
          if (onFilterClick) {
            onFilterClick([
              {
                id: 'billing-status-none',
                field: 'billingStatus',
                operator: 'in',
                value: [INVOICE_BILLING_STATUSES.None],
              },
            ]);
          }
        },
      },
      {
        label: 'Overdue',
        count: invoicesHighlightsSummary?.overdue.count || 0,
        total: invoicesHighlightsSummary?.overdue.amount || 0,
        icon: <ClockWatch />,
        iconBackground: '#FEEBEE',
        actionLabel: 'Review',
        countSelector: highlightsSelectors.overdueCount,
        amountSelector: highlightsSelectors.overdueAmount,
        onAction: () => {
          if (onFilterClick) {
            onFilterClick([
              {
                id: 'billing-status-overdue',
                field: 'billingStatus',
                operator: 'in',
                value: [INVOICE_BILLING_STATUSES.Overdue],
              },
            ]);
          }
        },
      },
      {
        label: 'Pending payment',
        count: invoicesHighlightsSummary?.pendingPayment.count || 0,
        total: invoicesHighlightsSummary?.pendingPayment.amount || 0,
        icon: <PendingArrowCircle />,
        iconBackground: '#FDF1D7',
        actionLabel: 'Review',
        countSelector: highlightsSelectors.pendingCount,
        amountSelector: highlightsSelectors.pendingAmount,
        onAction: () => {
          if (onFilterClick) {
            onFilterClick([
              {
                id: 'billing-status-pending',
                field: 'billingStatus',
                operator: 'in',
                value: [INVOICE_BILLING_STATUSES.PendingPayment],
              },
            ]);
          }
        },
      },
    ],
    [invoicesHighlightsSummary, onFilterClick],
  );

  if (!dateDisplay) {
    return null;
  }

  return (
    <Suspense fallback={<Skeleton variant="rectangular" height={120} sx={{ borderRadius: '12px', mb: 2 }} />}>
      <Box
        sx={{
          overflowX: 'auto',
          overflowY: 'hidden',
          mb: 2,
        }}
      >
        <Paper
          {...getSelectorAttribute(highlightsSelectors.root)}
          elevation={0}
          sx={{
            borderRadius: '12px',
            p: 3,
            backgroundColor: '#FAFBFF',
            border: '1px solid #CFE6FF',
            minWidth: 'max-content',
            height: '92px',
          }}
        >
          <Stack
            direction="row"
            spacing={3}
            divider={<Divider orientation="vertical" flexItem sx={{ borderColor: '#D6DBE7' }} />}
          >
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center"
              }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '14px',
                  color: '#020F20',
                  fontWeight: 400,
                }}
              >
                {monthName ? `Total ${monthName} invoices` : 'Total selected invoices'}
              </Typography>
              <Stack direction="row" spacing={2} sx={{
                alignItems: "baseline"
              }}>
                <Typography
                  {...getSelectorAttribute(highlightsSelectors.totalCount)}
                  component="span"
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#020F20',
                  }}
                >
                  {invoicesCount}
                </Typography>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ height: 12, borderColor: '#020F20', position: 'relative', top: '5px' }}
                />
                <Price
                  {...getSelectorAttribute(highlightsSelectors.totalAmount)}
                  rounded
                  variant="body1"
                  fractionDigits={0}
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#020F20',
                  }}
                >
                  {invoicesHighlightsSummary?.totalAmount || 0}
                </Price>
              </Stack>
            </Box>

            {panels.map((panel) => (
              <Box
                key={panel.label}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  justifyContent: "space-between",
                  position: "relative",
                  flex: 1,
                  minWidth: 'fit-content'
                }}>
                <Stack direction="row" spacing={1} sx={{
                  alignItems: "center"
                }}>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                      alignItems: "center",
                      mb: 1.5
                    }}>
                    <Box
                      sx={{
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {panel.icon}
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '14px',
                        color: '#7E8796',
                        fontWeight: 400,
                      }}
                    >
                      {panel.label}
                    </Typography>
                  </Stack>

                  {panel.actionLabel && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={panel.onAction}
                      endIcon={<ChevronRight style={{ width: '4px', height: '8px' }} />}
                      sx={{
                        color: '#020F20',
                        fontSize: '12px',
                        backgroundColor: '#E8EBF2',
                        borderRadius: '100px',
                        height: '21px',
                        padding: '0 6px',
                        minWidth: 'auto',
                        '& .MuiButton-endIcon': {
                          marginLeft: '10px',
                          marginRight: '4px',
                        },
                        '&:hover': {
                          backgroundColor: '#E5E5E5',
                        },
                      }}
                    >
                      {panel.actionLabel}
                    </Button>
                  )}
                </Stack>

                <Stack direction="row" spacing={2} sx={{
                  alignItems: "baseline"
                }}>
                  <Typography
                    {...getSelectorAttribute(panel.countSelector)}
                    variant="body2"
                    sx={{
                      fontSize: '14px',
                      color: '#020F20',
                      fontWeight: 400,
                    }}
                  >
                    {panel.count} {panel.count === 1 ? 'invoice' : 'invoices'}
                  </Typography>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ height: 13, borderColor: '#020F20', position: 'relative', top: '4px' }}
                  />
                  <Price
                    {...getSelectorAttribute(panel.amountSelector)}
                    rounded
                    variant="body2"
                    fractionDigits={0}
                    sx={{
                      fontSize: '14px',
                      color: '#020F20',
                    }}
                  >
                    {panel.total}
                  </Price>
                </Stack>
              </Box>
            ))}
          </Stack>
        </Paper>
      </Box>
    </Suspense>
  );
};
